import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import gameSound from '../../utils/gameSound';
import {
  HWATU_DECK,
  getHwatuImageUrl,
  SCORING_RULES,
} from './GoStopSpec';

// ==================== 리더보드 시스템 ====================
// 서버 기반 리더보드 (로컬 캐시 포함)
const Leaderboard = {
  cache: {},

  // 로컬 스토리지에서 점수 가져오기 (캐시 또는 폴백)
  getScores(gameId) {
    // 캐시가 있으면 캐시 사용
    if (this.cache[gameId]) {
      return this.cache[gameId];
    }
    // 없으면 로컬 스토리지에서 가져오기
    const data = localStorage.getItem(`leaderboard_${gameId}`);
    return data ? JSON.parse(data) : [];
  },

  // 서버에서 점수 가져오기
  async fetchScores(gameId) {
    try {
      const response = await api.get(`/api/games/scores/${gameId}`);
      const scores = response.data.scores.map(s => ({
        id: s.id,
        name: s.playerName,
        userName: s.userName,
        score: s.score,
        date: s.createdAt
      }));
      this.cache[gameId] = scores;
      localStorage.setItem(`leaderboard_${gameId}`, JSON.stringify(scores));
      return scores;
    } catch (error) {
      console.error('Failed to fetch scores:', error);
      return this.getScores(gameId);
    }
  },

  // 서버에 점수 저장
  async addScore(gameId, name, score, details = {}) {
    try {
      const response = await api.post('/api/games/scores', {
        gameId,
        playerName: name || '익명',
        score,
        details
      });
      // 캐시 새로고침
      await this.fetchScores(gameId);
      return response.data.rank;
    } catch (error) {
      console.error('Failed to save score:', error);
      // 서버 실패 시 로컬에만 저장
      const scores = this.getScores(gameId);
      const newEntry = {
        id: Date.now(),
        name: name || '익명',
        score,
        details,
        date: new Date().toISOString()
      };
      scores.push(newEntry);
      scores.sort((a, b) => b.score - a.score);
      const top10 = scores.slice(0, 10);
      localStorage.setItem(`leaderboard_${gameId}`, JSON.stringify(top10));
      return top10.findIndex(s => s.id === newEntry.id) + 1;
    }
  },

  getRank(gameId, score) {
    const scores = this.getScores(gameId);
    const rank = scores.filter(s => s.score > score).length + 1;
    return rank;
  },

  // 모든 게임 점수 가져오기
  async fetchAllScores() {
    try {
      const response = await api.get('/api/games/scores');
      const rankings = response.data.rankings;
      for (const gameId in rankings) {
        const scores = rankings[gameId].map(s => ({
          id: s.id,
          name: s.playerName,
          userName: s.userName,
          score: s.score,
          date: s.createdAt
        }));
        this.cache[gameId] = scores;
        localStorage.setItem(`leaderboard_${gameId}`, JSON.stringify(scores));
      }
      return true;
    } catch (error) {
      console.error('Failed to fetch all scores:', error);
      return false;
    }
  }
};

// 사운드 토글 버튼 컴포넌트
const SoundToggle = ({ isMuted, onToggle }) => (
  <button
    className={`w-10 h-10 rounded-full border-none text-xl cursor-pointer transition-all flex items-center justify-center flex-shrink-0 ${isMuted ? 'bg-red-100' : 'bg-gray-100 hover:bg-gray-200'}`}
    onClick={onToggle}
    title={isMuted ? '소리 켜기' : '소리 끄기'}
  >
    {isMuted ? '🔇' : '🔊'}
  </button>
);

// 리더보드 표시 컴포넌트
const LeaderboardDisplay = ({ gameId, gameName, onClose }) => {
  const [scores, setScores] = useState(Leaderboard.getScores(gameId));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchScores = async () => {
      const data = await Leaderboard.fetchScores(gameId);
      setScores(data);
      setLoading(false);
    };
    fetchScores();
  }, [gameId]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 max-w-md w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-semibold text-gray-900 m-0">🏆 {gameName} 랭킹</h3>
          <button className="bg-transparent border-none text-2xl text-gray-400 cursor-pointer p-1 hover:text-gray-700" onClick={onClose}>×</button>
        </div>
        <div className="mb-4">
          {loading ? (
            <p className="text-center text-gray-500 py-8">로딩 중...</p>
          ) : scores.length === 0 ? (
            <p className="text-center text-gray-500 py-8">아직 기록이 없습니다.</p>
          ) : (
            <table className="w-full border-collapse mb-6">
              <thead>
                <tr>
                  <th className="py-3 px-3 text-left border-b border-gray-200 font-semibold text-gray-500 text-sm uppercase">순위</th>
                  <th className="py-3 px-3 text-left border-b border-gray-200 font-semibold text-gray-500 text-sm uppercase">이름</th>
                  <th className="py-3 px-3 text-left border-b border-gray-200 font-semibold text-gray-500 text-sm uppercase">점수</th>
                  <th className="py-3 px-3 text-left border-b border-gray-200 font-semibold text-gray-500 text-sm uppercase">날짜</th>
                </tr>
              </thead>
              <tbody>
                {scores.map((entry, idx) => (
                  <tr key={entry.id} className={idx < 3 ? idx === 0 ? 'bg-gradient-to-r from-yellow-100 to-yellow-200' : idx === 1 ? 'bg-gradient-to-r from-gray-100 to-gray-200' : 'bg-gradient-to-r from-orange-100 to-orange-200' : ''}>
                    <td className="py-3 px-3 text-gray-900 border-b border-gray-200 last:border-b-0">
                      {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                    </td>
                    <td className="py-3 px-3 text-gray-900 border-b border-gray-200 last:border-b-0">{entry.userName || entry.name}</td>
                    <td className="py-3 px-3 text-gray-900 border-b border-gray-200 last:border-b-0 font-semibold">{entry.score.toLocaleString()}</td>
                    <td className="py-3 px-3 text-gray-900 border-b border-gray-200 last:border-b-0 text-sm">{new Date(entry.date).toLocaleDateString('ko-KR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// 점수 등록 모달
const ScoreSubmitModal = ({ score, rank, onSubmit, onClose }) => {
  const [name, setName] = useState(localStorage.getItem('playerName') || '');

  const handleSubmit = () => {
    localStorage.setItem('playerName', name);
    onSubmit(name);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
        <h3 className="text-2xl font-semibold text-gray-900 mb-6">🎉 게임 완료!</h3>
        <p className="text-2xl mb-2">점수: <strong className="text-blue-500">{score.toLocaleString()}</strong></p>
        {rank <= 10 && <p className="text-amber-500 font-semibold mb-6">🏆 {rank}위 달성!</p>}
        <div className="flex gap-3 mb-4">
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="이름 입력"
            maxLength={10}
            className="flex-1 py-3 px-4 border-2 border-gray-200 rounded-lg text-base text-center focus:outline-none focus:border-blue-500"
          />
          <button onClick={handleSubmit} className="bg-gray-900 text-white border-none py-3 px-6 rounded-lg text-base font-medium cursor-pointer hover:bg-gray-700">등록</button>
        </div>
        <button onClick={onClose} className="bg-transparent border-none text-gray-400 text-sm cursor-pointer p-2 hover:text-gray-600">건너뛰기</button>
      </div>
    </div>
  );
};

// ==================== 메모리 카드 게임 ====================
const MemoryGame = ({ onBack }) => {
  const emojis = ['🎮', '🎯', '🎲', '🎪', '🎨', '🎭', '🎸', '🎺'];
  const [cards, setCards] = useState(() => {
    const shuffled = [...emojis, ...emojis]
      .sort(() => Math.random() - 0.5)
      .map((emoji, index) => ({ id: index, emoji, flipped: false, matched: false }));
    return shuffled;
  });
  const [flippedCards, setFlippedCards] = useState([]);
  const [moves, setMoves] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [isMuted, setIsMuted] = useState(gameSound.getMuted());
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [finalRank, setFinalRank] = useState(0);

  useEffect(() => {
    gameSound.init();
    gameSound.playGameStart();
  }, []);

  const calculateScore = (moves) => Math.max(1000 - (moves * 50), 100);

  const handleCardClick = (id) => {
    if (isChecking) return;
    const card = cards.find(c => c.id === id);
    if (card.flipped || card.matched) return;

    gameSound.playFlip();

    const newCards = cards.map(c =>
      c.id === id ? { ...c, flipped: true } : c
    );
    setCards(newCards);

    const newFlipped = [...flippedCards, id];
    setFlippedCards(newFlipped);

    if (newFlipped.length === 2) {
      const newMoves = moves + 1;
      setMoves(newMoves);
      setIsChecking(true);
      const [first, second] = newFlipped;
      const firstCard = newCards.find(c => c.id === first);
      const secondCard = newCards.find(c => c.id === second);

      if (firstCard.emoji === secondCard.emoji) {
        gameSound.playMatch();
        const updatedCards = newCards.map(c =>
          c.id === first || c.id === second ? { ...c, matched: true } : c
        );
        setCards(updatedCards);
        setFlippedCards([]);
        setIsChecking(false);

        if (updatedCards.every(c => c.matched)) {
          setTimeout(() => {
            gameSound.playWin();
            const score = calculateScore(newMoves);
            setFinalScore(score);
            setFinalRank(Leaderboard.getRank('memory', score));
            setShowSubmit(true);
          }, 300);
        }
      } else {
        gameSound.playWrong();
        setTimeout(() => {
          setCards(newCards.map(c =>
            c.id === first || c.id === second ? { ...c, flipped: false } : c
          ));
          setFlippedCards([]);
          setIsChecking(false);
        }, 1000);
      }
    }
  };

  const resetGame = () => {
    gameSound.playClick();
    const shuffled = [...emojis, ...emojis]
      .sort(() => Math.random() - 0.5)
      .map((emoji, index) => ({ id: index, emoji, flipped: false, matched: false }));
    setCards(shuffled);
    setFlippedCards([]);
    setMoves(0);
    gameSound.playGameStart();
  };

  const handleScoreSubmit = (name) => {
    Leaderboard.addScore('memory', name, finalScore, { moves });
    setShowSubmit(false);
  };

  const toggleSound = () => {
    const muted = gameSound.toggleMute();
    setIsMuted(muted);
  };

  const isComplete = cards.every(c => c.matched);

  return (
    <div className="max-w-[600px] mx-auto p-4">
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
        <button onClick={() => { gameSound.playClick(); onBack(); }} className="bg-transparent border-none text-blue-500 text-base cursor-pointer p-2 rounded-lg hover:bg-blue-50">← 뒤로</button>
        <h2 className="text-2xl font-semibold text-gray-900 m-0">메모리 게임</h2>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowLeaderboard(true)} className="py-2 px-4 border-none rounded-full text-sm font-medium cursor-pointer bg-gradient-to-br from-amber-400 to-amber-500 text-gray-900 hover:scale-105 hover:shadow-lg">🏆</button>
          <span className="text-sm text-gray-500 bg-gray-100 py-2 px-4 rounded-full">시도: {moves}회</span>
          <SoundToggle isMuted={isMuted} onToggle={toggleSound} />
        </div>
      </div>

      {isComplete && !showSubmit && (
        <div className="text-center bg-gradient-to-br from-blue-50 to-green-50 p-8 rounded-2xl mb-6">
          <h3 className="text-2xl mb-2">🎉 축하합니다!</h3>
          <p className="text-gray-500 mb-4">{moves}번 만에 완료! (점수: {calculateScore(moves)})</p>
          <button onClick={resetGame} className="bg-gray-900 text-white border-none py-3 px-6 rounded-lg text-base font-medium cursor-pointer hover:bg-gray-700">다시 하기</button>
        </div>
      )}

      <div className="grid grid-cols-4 gap-3 mb-4">
        {cards.map(card => (
          <div
            key={card.id}
            className="aspect-square cursor-pointer"
            style={{ perspective: '1000px' }}
            onClick={() => handleCardClick(card.id)}
          >
            <div className={`relative w-full h-full transition-transform duration-500 ${card.flipped || card.matched ? '[transform:rotateY(180deg)]' : ''}`} style={{ transformStyle: 'preserve-3d' }}>
              <div className="absolute w-full h-full flex items-center justify-center rounded-xl text-3xl font-semibold bg-gradient-to-br from-blue-500 to-purple-500 text-white" style={{ backfaceVisibility: 'hidden' }}>?</div>
              <div className={`absolute w-full h-full flex items-center justify-center rounded-xl text-3xl font-semibold bg-white border-2 ${card.matched ? 'bg-green-50 border-green-500' : 'border-gray-200'}`} style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>{card.emoji}</div>
            </div>
          </div>
        ))}
      </div>

      <button onClick={resetGame} className="block mx-auto mt-6 bg-gray-900 text-white border-none py-3 px-6 rounded-lg text-base font-medium cursor-pointer hover:bg-gray-700">새 게임</button>

      {showLeaderboard && (
        <LeaderboardDisplay gameId="memory" gameName="메모리 게임" onClose={() => setShowLeaderboard(false)} />
      )}
      {showSubmit && (
        <ScoreSubmitModal
          score={finalScore}
          rank={finalRank}
          onSubmit={handleScoreSubmit}
          onClose={() => setShowSubmit(false)}
        />
      )}
    </div>
  );
};

// ==================== 틱택토 게임 ====================
const TicTacToe = ({ onBack }) => {
  const [board, setBoard] = useState(Array(9).fill(null));
  const [isXNext, setIsXNext] = useState(true);
  const [scores, setScores] = useState({ X: 0, O: 0 });
  const [isMuted, setIsMuted] = useState(gameSound.getMuted());
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  useEffect(() => {
    gameSound.init();
    gameSound.playGameStart();
  }, []);

  const calculateWinner = (squares) => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8],
      [0, 3, 6], [1, 4, 7], [2, 5, 8],
      [0, 4, 8], [2, 4, 6]
    ];
    for (let [a, b, c] of lines) {
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return { winner: squares[a], line: [a, b, c] };
      }
    }
    return null;
  };

  const result = calculateWinner(board);
  const winner = result?.winner;
  const winningLine = result?.line || [];
  const isDraw = !winner && board.every(cell => cell !== null);

  const handleClick = (index) => {
    if (board[index] || winner) return;
    gameSound.playClick();
    const newBoard = [...board];
    newBoard[index] = isXNext ? 'X' : 'O';
    setBoard(newBoard);
    setIsXNext(!isXNext);

    const newResult = calculateWinner(newBoard);
    if (newResult?.winner) {
      setTimeout(() => gameSound.playWin(), 100);
      const newScores = { ...scores, [newResult.winner]: scores[newResult.winner] + 1 };
      setScores(newScores);
      Leaderboard.addScore('tictactoe', newResult.winner, newScores[newResult.winner]);
    } else if (newBoard.every(cell => cell !== null)) {
      gameSound.playDraw();
    }
  };

  const resetGame = () => {
    gameSound.playClick();
    setBoard(Array(9).fill(null));
    setIsXNext(true);
  };

  const toggleSound = () => {
    const muted = gameSound.toggleMute();
    setIsMuted(muted);
  };

  return (
    <div className="max-w-[600px] mx-auto p-4">
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
        <button onClick={() => { gameSound.playClick(); onBack(); }} className="bg-transparent border-none text-blue-500 text-base cursor-pointer p-2 rounded-lg hover:bg-blue-50">← 뒤로</button>
        <h2 className="text-2xl font-semibold text-gray-900 m-0">틱택토</h2>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowLeaderboard(true)} className="py-2 px-4 border-none rounded-full text-sm font-medium cursor-pointer bg-gradient-to-br from-amber-400 to-amber-500 text-gray-900 hover:scale-105">🏆</button>
          <span className="text-sm text-gray-500 bg-gray-100 py-2 px-4 rounded-full">X: {scores.X} | O: {scores.O}</span>
          <SoundToggle isMuted={isMuted} onToggle={toggleSound} />
        </div>
      </div>

      <div className="text-center text-xl font-medium mb-6 text-gray-900">
        {winner ? `🎉 ${winner} 승리!` : isDraw ? '무승부!' : `다음 차례: ${isXNext ? 'X' : 'O'}`}
      </div>

      <div className="grid grid-cols-3 gap-2 max-w-[300px] mx-auto">
        {board.map((cell, index) => (
          <button
            key={index}
            className={`aspect-square bg-gray-100 border-none rounded-xl text-4xl font-bold cursor-pointer transition-all hover:bg-gray-200 ${cell === 'X' ? 'text-blue-500' : 'text-red-500'} ${winningLine.includes(index) ? 'bg-amber-100 animate-pulse' : ''}`}
            onClick={() => handleClick(index)}
          >
            {cell}
          </button>
        ))}
      </div>

      <div className="flex gap-4 justify-center mt-6">
        <button onClick={resetGame} className="bg-gray-900 text-white border-none py-3 px-6 rounded-lg text-base font-medium cursor-pointer hover:bg-gray-700">새 게임</button>
        <button onClick={() => { setScores({ X: 0, O: 0 }); resetGame(); }} className="bg-gray-100 text-gray-700 border-none py-3 px-6 rounded-lg text-base font-medium cursor-pointer hover:bg-gray-200">점수 초기화</button>
      </div>

      {showLeaderboard && (
        <LeaderboardDisplay gameId="tictactoe" gameName="틱택토" onClose={() => setShowLeaderboard(false)} />
      )}
    </div>
  );
};

// ==================== 수도쿠 게임 ====================
const MAX_ERRORS = 15; // 최대 실패 횟수

const Sudoku = ({ onBack }) => {
  const [isMuted, setIsMuted] = useState(gameSound.getMuted());
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [difficulty, setDifficulty] = useState('easy');
  const [board, setBoard] = useState([]);
  const [solution, setSolution] = useState([]);
  const [initial, setInitial] = useState([]);
  const [selected, setSelected] = useState(null);
  const [errors, setErrors] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [finalRank, setFinalRank] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // 수도쿠 생성 함수
  const generateSudoku = useCallback(() => {
    // 간단한 수도쿠 생성 (완전한 알고리즘은 복잡하므로 미리 정의된 패턴 사용)
    const baseSolution = [
      [5,3,4,6,7,8,9,1,2],
      [6,7,2,1,9,5,3,4,8],
      [1,9,8,3,4,2,5,6,7],
      [8,5,9,7,6,1,4,2,3],
      [4,2,6,8,5,3,7,9,1],
      [7,1,3,9,2,4,8,5,6],
      [9,6,1,5,3,7,2,8,4],
      [2,8,7,4,1,9,6,3,5],
      [3,4,5,2,8,6,1,7,9]
    ];

    // 행/열/숫자 섞기로 변형
    const shuffle = (arr) => {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    };

    const numMap = shuffle([1,2,3,4,5,6,7,8,9]);
    const newSolution = baseSolution.map(row =>
      row.map(n => numMap[n - 1])
    );

    // 난이도에 따라 빈칸 수 결정
    const emptyCount = difficulty === 'easy' ? 30 : difficulty === 'medium' ? 40 : 50;
    const newBoard = newSolution.map(row => [...row]);

    let removed = 0;
    while (removed < emptyCount) {
      const r = Math.floor(Math.random() * 9);
      const c = Math.floor(Math.random() * 9);
      if (newBoard[r][c] !== 0) {
        newBoard[r][c] = 0;
        removed++;
      }
    }

    setSolution(newSolution);
    setBoard(newBoard);
    setInitial(newBoard.map(row => row.map(n => n !== 0)));
    setSelected(null);
    setErrors(0);
    setStartTime(Date.now());
    setElapsedTime(0);
    setIsComplete(false);
    setIsGameOver(false);
  }, [difficulty]);

  useEffect(() => {
    gameSound.init();
    generateSudoku();
  }, [generateSudoku]);

  useEffect(() => {
    if (!isComplete && !isGameOver && startTime) {
      const timer = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [startTime, isComplete, isGameOver]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleCellClick = (row, col) => {
    if (initial[row]?.[col] || isGameOver || isComplete) return;
    setSelected({ row, col });
    gameSound.playClick();
  };

  const handleNumberInput = (num) => {
    if (!selected || initial[selected.row]?.[selected.col] || isGameOver || isComplete) return;

    const newBoard = board.map(row => [...row]);
    newBoard[selected.row][selected.col] = num;
    setBoard(newBoard);

    if (num !== 0) {
      if (solution[selected.row][selected.col] === num) {
        gameSound.playMatch();
        // 완료 체크
        const isSolved = newBoard.every((row, r) =>
          row.every((cell, c) => cell === solution[r][c])
        );
        if (isSolved) {
          gameSound.playWin();
          setIsComplete(true);
          const score = calculateScore();
          setFinalScore(score);
          setFinalRank(Leaderboard.getRank('sudoku', score));
          setShowSubmit(true);
        }
      } else {
        gameSound.playWrong();
        const newErrors = errors + 1;
        setErrors(newErrors);
        // 15회 실패 시 게임 오버
        if (newErrors >= MAX_ERRORS) {
          setIsGameOver(true);
          gameSound.playLose();
        }
      }
    }
  };

  const calculateScore = () => {
    const baseScore = difficulty === 'easy' ? 500 : difficulty === 'medium' ? 1000 : 1500;
    const timeBonus = Math.max(0, 300 - elapsedTime) * 2;
    const errorPenalty = errors * 50;
    return Math.max(baseScore + timeBonus - errorPenalty, 100);
  };

  const handleScoreSubmit = (name) => {
    Leaderboard.addScore('sudoku', name, finalScore, { difficulty, time: elapsedTime, errors });
    setShowSubmit(false);
  };

  const toggleSound = () => {
    const muted = gameSound.toggleMute();
    setIsMuted(muted);
  };

  const getCellClass = (row, col) => {
    let classes = 'w-[calc(100%/9)] aspect-square border border-sky-200 text-xl font-semibold text-slate-700 cursor-pointer transition-all flex items-center justify-center font-sans';
    if (initial[row]?.[col]) classes += ' bg-gradient-to-br from-blue-100 to-blue-200 text-orange-600 font-bold cursor-default';
    if (selected?.row === row && selected?.col === col) classes += ' bg-gradient-to-br from-sky-400 to-sky-500 text-white shadow-inner';
    else if (selected && (selected.row === row || selected.col === col)) classes += ' bg-gradient-to-br from-sky-200 to-blue-200';
    if (board[row]?.[col] && board[row][col] !== solution[row]?.[col]) classes += ' bg-gradient-to-br from-red-200 to-red-300 text-red-600';
    if ((row + 1) % 3 === 0 && row < 8) classes += ' border-b-[3px] border-b-sky-400';
    if ((col + 1) % 3 === 0 && col < 8) classes += ' border-r-[3px] border-r-sky-400';
    return classes;
  };

  return (
    <div className={`max-w-[600px] mx-auto p-4 text-center ${isFullscreen ? 'fixed inset-0 z-[9999] bg-gray-100 max-w-none overflow-y-auto' : ''}`}>
      <div className={`flex items-center justify-between mb-6 pb-4 border-b border-gray-200 ${isFullscreen ? 'sticky top-0 bg-white px-4 py-3 mb-0 z-[100]' : ''}`}>
        <button onClick={() => { gameSound.playClick(); isFullscreen ? setIsFullscreen(false) : onBack(); }} className="bg-transparent border-none text-blue-500 text-base cursor-pointer p-2 rounded-lg hover:bg-blue-50">
          {isFullscreen ? '✕ 닫기' : '← 뒤로'}
        </button>
        <h2 className="text-2xl font-semibold text-gray-900 m-0">수도쿠</h2>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowLeaderboard(true)} className="py-2 px-4 border-none rounded-full text-sm font-medium cursor-pointer bg-gradient-to-br from-amber-400 to-amber-500 text-gray-900 hover:scale-105">🏆</button>
          <span className="text-sm text-gray-500 bg-gray-100 py-2 px-4 rounded-full">{formatTime(elapsedTime)}</span>
          <button onClick={() => setIsFullscreen(!isFullscreen)} className="hidden md:flex w-9 h-9 rounded-full border-none bg-gray-100 text-lg cursor-pointer items-center justify-center hover:bg-gray-200" title="전체화면">
            {isFullscreen ? '⤓' : '⤢'}
          </button>
          <SoundToggle isMuted={isMuted} onToggle={toggleSound} />
        </div>
      </div>

      {/* 남은 기회 표시 */}
      <div className="flex items-center justify-center gap-4 mb-5 py-3.5 px-5 bg-white rounded-2xl max-w-[380px] mx-auto shadow-sm border border-red-500/10">
        <span className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <span>❤️</span>기회
        </span>
        <div className="flex gap-[3px] flex-1 justify-center">
          {[...Array(MAX_ERRORS)].map((_, i) => (
            <span key={i} className={`w-3 h-3 rounded-full transition-all ${i < MAX_ERRORS - errors ? 'bg-gradient-to-br from-red-500 to-red-600 shadow-sm' : 'bg-gray-200 scale-[0.8]'}`} />
          ))}
        </div>
        <span className="text-base font-bold text-red-600 min-w-[55px] text-right tabular-nums">{MAX_ERRORS - errors}/{MAX_ERRORS}</span>
      </div>

      <div className="flex justify-center gap-3 mb-6 flex-wrap">
        <select value={difficulty} onChange={e => setDifficulty(e.target.value)} className="py-2.5 px-5 border-none rounded-xl bg-gradient-to-br from-sky-100 to-sky-200 text-sm font-medium text-sky-700 cursor-pointer outline-none hover:from-sky-200 hover:to-sky-300" disabled={isGameOver || isComplete}>
          <option value="easy">쉬움</option>
          <option value="medium">보통</option>
          <option value="hard">어려움</option>
        </select>
        <button onClick={generateSudoku} className="py-2.5 px-5 text-sm bg-gradient-to-br from-sky-500 to-sky-600 text-white border-none rounded-xl font-medium cursor-pointer hover:from-sky-600 hover:to-sky-700">새 게임</button>
      </div>

      {/* 게임 오버 표시 */}
      {isGameOver && (
        <div className="bg-white rounded-[20px] p-8 text-center mb-5 max-w-[380px] mx-auto shadow-lg border border-red-500/20 animate-[slideUp_0.4s_ease-out]">
          <h3 className="text-3xl font-bold text-red-600 mb-3">💔 게임 오버!</h3>
          <p className="text-gray-500 mb-6 text-base">15번의 실패로 게임이 종료되었습니다.</p>
          <button onClick={generateSudoku} className="bg-gradient-to-br from-red-500 to-red-600 text-white border-none py-3.5 px-8 rounded-xl text-base font-semibold cursor-pointer hover:from-red-600 hover:to-red-700 hover:scale-[1.02]">다시 도전</button>
        </div>
      )}

      <div className={`flex flex-col max-w-[380px] mx-auto mb-6 bg-gradient-to-br from-sky-100 via-sky-50 to-sky-100 border-[3px] border-sky-300 rounded-2xl p-1.5 shadow-lg ${isGameOver ? 'opacity-40 pointer-events-none blur-[1px]' : ''}`}>
        {board.map((row, r) => (
          <div key={r} className="flex">
            {row.map((cell, c) => (
              <div
                key={c}
                className={getCellClass(r, c)}
                onClick={() => handleCellClick(r, c)}
              >
                {cell !== 0 ? cell : ''}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="flex justify-center gap-2 flex-wrap max-w-[380px] mx-auto">
        {[1,2,3,4,5,6,7,8,9].map(num => (
          <button key={num} className="w-10 h-10 border-none rounded-xl bg-gradient-to-br from-sky-100 to-sky-200 text-xl font-semibold text-sky-700 cursor-pointer transition-all shadow-sm hover:from-sky-300 hover:to-sky-400 hover:text-white hover:-translate-y-0.5 hover:shadow-md" onClick={() => handleNumberInput(num)}>
            {num}
          </button>
        ))}
        <button className="w-auto px-5 h-10 border-none rounded-xl bg-gradient-to-br from-red-200 to-red-300 text-base font-semibold text-red-600 cursor-pointer hover:from-red-400 hover:to-red-500 hover:text-white" onClick={() => handleNumberInput(0)}>⌫</button>
      </div>

      {showLeaderboard && (
        <LeaderboardDisplay gameId="sudoku" gameName="수도쿠" onClose={() => setShowLeaderboard(false)} />
      )}
      {showSubmit && (
        <ScoreSubmitModal
          score={finalScore}
          rank={finalRank}
          onSubmit={handleScoreSubmit}
          onClose={() => setShowSubmit(false)}
        />
      )}
    </div>
  );
};

// ==================== 맞고 게임 ====================
// 화투 데이터는 GoStopSpec.js에서 import

const GoStop = ({ onBack }) => {
  const [isMuted, setIsMuted] = useState(gameSound.getMuted());
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [gameState, setGameState] = useState('betting');
  const [chips, setChips] = useState(1000);
  const [currentBet, setCurrentBet] = useState(100);
  const [message, setMessage] = useState('베팅 금액을 선택하세요');
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [deck, setDeck] = useState([]);
  const [playerHand, setPlayerHand] = useState([]);
  const [computerHand, setComputerHand] = useState([]);
  const [fieldCards, setFieldCards] = useState([]);

  const [playerCollected, setPlayerCollected] = useState({ 광: [], 열끗: [], 띠: [], 피: [] });
  const [computerCollected, setComputerCollected] = useState({ 광: [], 열끗: [], 띠: [], 피: [] });

  const [playerScore, setPlayerScore] = useState(0);
  const [computerScore, setComputerScore] = useState(0);
  const [goCount, setGoCount] = useState(0);
  const [canStop, setCanStop] = useState(false);
  const [isPlayerTurn, setIsPlayerTurn] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);

  const [showSubmit, setShowSubmit] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [finalRank, setFinalRank] = useState(0);
  const [scoreBreakdown, setScoreBreakdown] = useState([]);

  // 애니메이션 상태
  const [playingCard, setPlayingCard] = useState(null);
  const [computerPlayingCard, setComputerPlayingCard] = useState(null);
  const [matchedCards, setMatchedCards] = useState([]);
  const [showMatchEffect, setShowMatchEffect] = useState(false);
  const [matchEffectText, setMatchEffectText] = useState('매칭!');

  // 카드 선택 상태 (바닥에 2장 있을 때)
  const [pendingSelection, setPendingSelection] = useState(null);

  useEffect(() => {
    gameSound.init();
  }, []);

  // 덱 섞기
  const shuffleDeck = () => {
    const newDeck = HWATU_DECK.map((card, idx) => ({
      ...card,
      id: `card-${idx}-${Date.now()}`
    }));
    for (let i = newDeck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newDeck[i], newDeck[j]] = [newDeck[j], newDeck[i]];
    }
    return newDeck;
  };

  // 점수 계산 (GoStopSpec의 SCORING_RULES 사용)
  const calculateScore = useCallback((collected) => {
    // collected 객체를 flat array로 변환
    const allCards = [
      ...collected.광,
      ...collected.열끗,
      ...collected.띠,
      ...collected.피,
    ];

    let score = 0;
    const breakdown = [];

    for (const rule of SCORING_RULES) {
      if (rule.checkFn(allCards)) {
        const ruleScore = rule.scoreFn
          ? rule.scoreFn(allCards)
          : rule.score;

        if (ruleScore > 0) {
          // 동적 점수 규칙의 경우 상세 정보 추가
          let name = rule.name;
          if (rule.id === 'yeolkkeut') {
            name = `열끗 ${collected.열끗.length}장`;
          } else if (rule.id === 'ddi') {
            name = `띠 ${collected.띠.length}장`;
          } else if (rule.id === 'pi') {
            const piCount = collected.피.reduce((sum, c) => sum + c.piCount, 0);
            name = `피 ${piCount}장`;
          }

          breakdown.push({ name, score: ruleScore });
          score += ruleScore;
        }
      }
    }

    return { score, breakdown };
  }, []);

  // 게임 시작
  const startGame = () => {
    if (currentBet > chips) {
      setMessage('칩이 부족합니다!');
      return;
    }

    gameSound.playGameStart();
    const newDeck = shuffleDeck();

    // 카드 배분: 플레이어 7장, 컴퓨터 7장, 바닥 6장
    setPlayerHand(newDeck.slice(0, 7));
    setComputerHand(newDeck.slice(7, 14));
    setFieldCards(newDeck.slice(14, 20));
    setDeck(newDeck.slice(20));

    setPlayerCollected({ 광: [], 열끗: [], 띠: [], 피: [] });
    setComputerCollected({ 광: [], 열끗: [], 띠: [], 피: [] });
    setPlayerScore(0);
    setComputerScore(0);
    setGoCount(0);
    setCanStop(false);
    setIsPlayerTurn(true);
    setSelectedCard(null);
    setScoreBreakdown([]);
    setGameState('playing');
    setMessage('카드를 선택하세요');
  };

  // 카드 선택
  const selectCard = (card) => {
    if (!isPlayerTurn || canStop || playingCard || pendingSelection) return;
    setSelectedCard(card);
    gameSound.playClick();
  };

  // 더블클릭으로 바로 카드 내기
  const handleDoubleClick = (card) => {
    if (!isPlayerTurn || canStop || playingCard || pendingSelection) return;
    setSelectedCard(card);
    setTimeout(() => playCardWithAnimation(card), 50);
  };

  // 카드 내기 (애니메이션 포함)
  const playCardWithAnimation = (cardToPlay) => {
    const card = cardToPlay || selectedCard;
    if (!card || !isPlayerTurn || canStop || playingCard) return;

    // 애니메이션 시작
    setPlayingCard(card);
    gameSound.playFlip();

    const matchingCards = fieldCards.filter(f => f.month === card.month);

    // 매칭되는 카드 하이라이트 (0.5초 후)
    setTimeout(() => {
      if (matchingCards.length > 0) {
        setMatchedCards(matchingCards.map(c => c.id));
        setMatchEffectText(matchingCards.length === 3 ? '싹쓸이!' : '매칭!');
      }
    }, 500);

    // 애니메이션 후 실제 카드 내기 처리 (1초 후)
    setTimeout(() => {
      processPlayCard(card, matchingCards);
    }, 1000);
  };

  // 실제 카드 처리 로직
  const processPlayCard = (card, matchingCards, selectedFloorCard = null) => {
    let newFieldCards = [...fieldCards];
    let newCollected = JSON.parse(JSON.stringify(playerCollected));
    let newHand = playerHand.filter(c => c.id !== card.id);

    if (matchingCards.length === 0) {
      // 매칭 카드 없음 - 바닥에 놓기
      newFieldCards.push(card);
    } else if (matchingCards.length === 1) {
      // 1장 매칭 - 둘 다 가져오기
      const matched = matchingCards[0];
      newFieldCards = fieldCards.filter(f => f.id !== matched.id);
      newCollected[card.type].push(card);
      newCollected[matched.type].push(matched);
      setShowMatchEffect(true);
      gameSound.playMatch();
    } else if (matchingCards.length === 2) {
      // 2장 매칭 - 선택 필요
      if (!selectedFloorCard) {
        // 선택 UI 표시
        setPendingSelection({
          playedCard: card,
          matchingCards: matchingCards,
          newHand: newHand
        });
        setPlayingCard(null);
        setMatchedCards([]);
        setMessage('가져갈 카드를 선택하세요');
        return; // 여기서 멈추고 사용자 선택 대기
      } else {
        // 선택된 카드로 매칭
        const matched = selectedFloorCard;
        newFieldCards = fieldCards.filter(f => f.id !== matched.id);
        newCollected[card.type].push(card);
        newCollected[matched.type].push(matched);
        setShowMatchEffect(true);
        gameSound.playMatch();
      }
    } else if (matchingCards.length === 3) {
      // 3장 매칭 - 모두 가져오기 (뻑)
      newFieldCards = fieldCards.filter(f => f.month !== card.month);
      newCollected[card.type].push(card);
      matchingCards.forEach(m => newCollected[m.type].push(m));
      setShowMatchEffect(true);
      gameSound.playMatch();
    }

    // 이펙트 표시 후 처리 (0.8초 후)
    setTimeout(() => {
      setShowMatchEffect(false);
      setMatchedCards([]);
      setPlayingCard(null);

      // 덱에서 카드 뽑기
      if (deck.length > 0) {
        const drawnCard = deck[0];
        const newDeck = deck.slice(1);
        setDeck(newDeck);

        const drawnMatches = newFieldCards.filter(f => f.month === drawnCard.month);
        if (drawnMatches.length === 0) {
          newFieldCards.push(drawnCard);
        } else if (drawnMatches.length === 1) {
          const matched = drawnMatches[0];
          newFieldCards = newFieldCards.filter(f => f.id !== matched.id);
          newCollected[drawnCard.type].push(drawnCard);
          newCollected[matched.type].push(matched);
        } else if (drawnMatches.length === 2) {
          // 2장 매칭 - 첫 번째 카드 선택 (TODO: 선택 UI 추가 가능)
          const matched = drawnMatches[0];
          newFieldCards = newFieldCards.filter(f => f.id !== matched.id);
          newCollected[drawnCard.type].push(drawnCard);
          newCollected[matched.type].push(matched);
        } else if (drawnMatches.length === 3) {
          // 3장 매칭 - 싹쓸이! 모든 카드 획득
          drawnMatches.forEach(matched => {
            newFieldCards = newFieldCards.filter(f => f.id !== matched.id);
            newCollected[matched.type].push(matched);
          });
          newCollected[drawnCard.type].push(drawnCard);
        }
      }

      setPlayerHand(newHand);
      setFieldCards(newFieldCards);
      setPlayerCollected(newCollected);
      setSelectedCard(null);

      const { score, breakdown } = calculateScore(newCollected);
      setPlayerScore(score);
      setScoreBreakdown(breakdown);

      // 7점 이상이면 스톱 가능
      if (score >= 7) {
        setCanStop(true);
        setMessage(`${score}점! 고 또는 스톱?`);
        return;
      }

      // 게임 종료 체크
      if (newHand.length === 0) {
        endGame(newCollected, computerCollected);
        return;
      }

      // 컴퓨터 턴 (1초 후)
      setIsPlayerTurn(false);
      setMessage('컴퓨터 턴...');
      setTimeout(() => computerTurnWithAnimation(newFieldCards), 1000);
    }, 800);
  };

  // 카드 내기 버튼용
  const playCard = () => {
    playCardWithAnimation(selectedCard);
  };

  // 바닥 카드 선택 핸들러 (2장 매칭 시)
  const handleFloorCardSelection = (selectedFloorCard) => {
    if (!pendingSelection) return;

    gameSound.playClick();
    const { playedCard, matchingCards, newHand } = pendingSelection;

    // 선택한 카드로 다시 처리
    setPlayerHand(newHand);
    setPendingSelection(null);
    setMessage('처리 중...');

    // 선택된 카드로 매칭 처리
    processPlayCard(playedCard, matchingCards, selectedFloorCard);
  };

  // 컴퓨터 턴 (애니메이션 포함)
  const computerTurnWithAnimation = (currentFieldCards) => {
    if (computerHand.length === 0) {
      endGame(playerCollected, computerCollected);
      return;
    }

    // AI: 매칭 우선, 광/열끗/띠 우선
    let bestCard = computerHand[0];
    let bestScore = -1;

    for (const card of computerHand) {
      const matches = currentFieldCards.filter(f => f.month === card.month);
      let cardScore = 0;
      if (matches.length > 0) cardScore += 10;
      if (card.type === '광') cardScore += 5;
      else if (card.type === '열끗') cardScore += 3;
      else if (card.type === '띠') cardScore += 2;
      if (cardScore > bestScore) {
        bestScore = cardScore;
        bestCard = card;
      }
    }

    const matchingCards = currentFieldCards.filter(f => f.month === bestCard.month);

    // 1단계: 컴퓨터 카드 선택 애니메이션
    setComputerPlayingCard(bestCard);
    gameSound.playFlip();
    setMessage(`컴퓨터가 ${bestCard.month}월 ${bestCard.name} 카드를 냅니다`);

    // 2단계: 매칭 카드 하이라이트 (0.6초 후)
    setTimeout(() => {
      if (matchingCards.length > 0) {
        setMatchedCards(matchingCards.map(c => c.id));
        setMatchEffectText(matchingCards.length === 3 ? '싹쓸이!' : '매칭!');
        setShowMatchEffect(true);
        gameSound.playMatch();
      }
    }, 600);

    // 3단계: 실제 처리 (1.2초 후)
    setTimeout(() => {
      let newFieldCards = [...currentFieldCards];
      let newCollected = JSON.parse(JSON.stringify(computerCollected));
      let newHand = computerHand.filter(c => c.id !== bestCard.id);

      if (matchingCards.length === 0) {
        newFieldCards.push(bestCard);
      } else {
        const matched = matchingCards[0];
        newFieldCards = currentFieldCards.filter(f => f.id !== matched.id);
        if (matchingCards.length === 3) {
          newFieldCards = currentFieldCards.filter(f => f.month !== bestCard.month);
          matchingCards.forEach(m => newCollected[m.type].push(m));
        } else {
          newCollected[matched.type].push(matched);
        }
        newCollected[bestCard.type].push(bestCard);
      }

      // 4단계: 이펙트 정리 및 덱에서 뽑기 (0.6초 후)
      setTimeout(() => {
        setShowMatchEffect(false);
        setMatchedCards([]);
        setComputerPlayingCard(null);

        // 덱에서 뽑기
        if (deck.length > 0) {
          const drawnCard = deck[0];
          const newDeck = deck.slice(1);
          setDeck(newDeck);

          const drawnMatches = newFieldCards.filter(f => f.month === drawnCard.month);
          if (drawnMatches.length === 0) {
            newFieldCards.push(drawnCard);
          } else if (drawnMatches.length === 1) {
            const matched = drawnMatches[0];
            newFieldCards = newFieldCards.filter(f => f.id !== matched.id);
            newCollected[drawnCard.type].push(drawnCard);
            newCollected[matched.type].push(matched);
          } else if (drawnMatches.length === 2) {
            // 2장 매칭 - 첫 번째 카드 선택
            const matched = drawnMatches[0];
            newFieldCards = newFieldCards.filter(f => f.id !== matched.id);
            newCollected[drawnCard.type].push(drawnCard);
            newCollected[matched.type].push(matched);
          } else if (drawnMatches.length === 3) {
            // 3장 매칭 - 싹쓸이! 모든 카드 획득
            drawnMatches.forEach(matched => {
              newFieldCards = newFieldCards.filter(f => f.id !== matched.id);
              newCollected[matched.type].push(matched);
            });
            newCollected[drawnCard.type].push(drawnCard);
          }
        }

        setComputerHand(newHand);
        setFieldCards(newFieldCards);
        setComputerCollected(newCollected);

        const { score } = calculateScore(newCollected);
        setComputerScore(score);

        if (newHand.length === 0) {
          endGame(playerCollected, newCollected);
          return;
        }

        // 플레이어 턴으로 전환 (0.5초 후)
        setTimeout(() => {
          setIsPlayerTurn(true);
          setMessage('카드를 선택하세요');
        }, 500);
      }, 600);
    }, 1200);
  };

  // 기존 computerTurn (handleGo에서 사용)
  const computerTurn = (currentFieldCards) => {
    computerTurnWithAnimation(currentFieldCards);
  };

  // 고
  const handleGo = () => {
    setGoCount(g => g + 1);
    setCanStop(false);
    setMessage('고! 계속합니다');
    gameSound.playClick();
    setIsPlayerTurn(false);
    setTimeout(() => computerTurn(fieldCards), 800);
  };

  // 스톱
  const handleStop = () => {
    endGame(playerCollected, computerCollected);
  };

  // 게임 종료
  const endGame = (pCollected, cCollected) => {
    const { score: pScore, breakdown: pBreakdown } = calculateScore(pCollected);
    const { score: cScore } = calculateScore(cCollected);

    const finalPlayerScore = pScore * (goCount + 1);
    const finalComputerScore = cScore;

    setScoreBreakdown(pBreakdown);

    let winAmount = 0;
    if (finalPlayerScore > finalComputerScore && pScore >= 7) {
      winAmount = currentBet * (goCount + 1);
      setChips(c => c + winAmount);
      setMessage(`승리! +${winAmount} 칩`);
      gameSound.playWin();
    } else if (finalComputerScore > finalPlayerScore || pScore < 7) {
      winAmount = -currentBet;
      setChips(c => c + winAmount);
      setMessage(`패배! ${winAmount} 칩`);
      gameSound.playLose();
    } else {
      setMessage('무승부!');
      gameSound.playDraw();
    }

    setPlayerScore(finalPlayerScore);
    setComputerScore(finalComputerScore);
    setGameState('result');

    if (chips + winAmount >= 2000) {
      setFinalScore(chips + winAmount);
      setFinalRank(Leaderboard.getRank('gostop', chips + winAmount));
      setShowSubmit(true);
    }
  };

  const handleScoreSubmit = (name) => {
    Leaderboard.addScore('gostop', name, finalScore);
    setShowSubmit(false);
  };

  const toggleSound = () => {
    const muted = gameSound.toggleMute();
    setIsMuted(muted);
  };

  // 수집한 카드 개수
  const getCollectedCount = (collected) => ({
    광: collected.광.length,
    열끗: collected.열끗.length,
    띠: collected.띠.length,
    피: collected.피.reduce((sum, c) => sum + c.piCount, 0)
  });

  // 화투 카드 렌더링 컴포넌트 - 실제 이미지 사용
  const HwatuCard = ({ card, isSelected, isDisabled, onClick, onDoubleClick, size = 'normal', isPlaying, isMatched }) => {
    const imageUrl = getHwatuImageUrl(card.month, card.imageIndex);

    const sizeClasses = {
      normal: 'w-[60px] h-[90px] rounded-md',
      small: 'w-[52px] h-[78px] rounded-md',
      mini: 'w-10 h-[60px] rounded cursor-default'
    };

    return (
      <div
        className={`${sizeClasses[size]} bg-gray-900 cursor-pointer transition-all shadow-lg border-2 border-red-600 overflow-hidden relative ${isSelected ? '-translate-y-2.5 scale-105 shadow-2xl border-blue-500' : ''} ${isDisabled ? 'opacity-60 cursor-default' : 'hover:-translate-y-1.5 hover:scale-[1.02] hover:shadow-xl'} ${isPlaying ? 'animate-[cardPlay_0.8s_ease-out_forwards] z-[100]' : ''} ${isMatched ? 'animate-[cardMatch_0.7s_ease-in-out_infinite] z-50' : ''}`}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        style={onClick && !isDisabled ? { cursor: 'pointer' } : {}}
      >
        <div className="h-full w-full relative">
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
            {imageUrl ? (
              <img src={imageUrl} alt={`${card.month}월 ${card.name}`} className="w-full h-full object-cover block" />
            ) : (
              <span className="text-sm text-gray-500 font-semibold">{card.month}월</span>
            )}
          </div>
          {size !== 'mini' && (
            <div className="absolute bottom-0 left-0 right-0 p-0.5 bg-gradient-to-t from-black/70 to-transparent flex justify-center">
              <span className={`text-[0.5rem] font-bold py-0.5 px-1.5 rounded-md ${card.type === '광' ? 'bg-gradient-to-br from-amber-400 to-amber-500 text-gray-900' : card.type === '열끗' ? 'bg-gradient-to-br from-red-500 to-red-600 text-white' : card.type === '띠' ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                {card.type === '열끗' ? '열' : card.type}
              </span>
            </div>
          )}
          {isPlaying && <div className="absolute inset-0 bg-radial-gradient from-white/90 to-transparent animate-[playGlow_0.8s_ease-out] pointer-events-none" />}
          {isMatched && <div className="absolute -inset-1.5 border-4 border-amber-400 rounded-lg animate-[matchPulse_0.7s_ease-in-out_infinite] pointer-events-none" />}
        </div>
      </div>
    );
  };

  return (
    <div className={`max-w-[800px] mx-auto p-6 font-sans ${isFullscreen ? 'fixed inset-0 z-[9999] bg-gray-100 max-w-none p-0 overflow-y-auto' : ''}`}>
      {/* 헤더 - FileUpload 스타일 */}
      <header className="text-center mb-6">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => { gameSound.playClick(); isFullscreen ? setIsFullscreen(false) : onBack(); }} className="bg-transparent border-none text-blue-500 text-base cursor-pointer p-2 rounded-lg hover:bg-blue-50">
            {isFullscreen ? '✕ 닫기' : '← 뒤로'}
          </button>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 m-0">맞고</h1>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowLeaderboard(true)} className="py-2 px-4 border-none rounded-full text-sm font-medium cursor-pointer bg-gradient-to-br from-amber-400 to-amber-500 text-gray-900 hover:scale-105">🏆</button>
            <button onClick={() => setIsFullscreen(!isFullscreen)} className="hidden md:flex w-9 h-9 rounded-full border-none bg-gray-100 text-lg cursor-pointer items-center justify-center hover:bg-gray-200" title="전체화면">
              {isFullscreen ? '⤓' : '⤢'}
            </button>
            <SoundToggle isMuted={isMuted} onToggle={toggleSound} />
          </div>
        </div>
        <div className="inline-flex items-center gap-2 bg-gradient-to-br from-amber-100 to-amber-200 py-2 px-5 rounded-full shadow-sm">
          <span className="text-xl">💰</span>
          <span className="text-xl font-bold text-amber-800">{chips.toLocaleString()}</span>
          <span className="text-sm text-amber-600 font-medium">칩</span>
        </div>
      </header>

      {/* 메시지 바 */}
      <div className="bg-gradient-to-br from-gray-100 to-white rounded-2xl py-3.5 px-5 mb-6 shadow-sm">
        <p className="m-0 text-base font-semibold text-gray-900 text-center">{message}</p>
      </div>

      {/* 베팅 화면 - FileUpload 카드 스타일 */}
      {gameState === 'betting' && (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-5">
            <div className="bg-white rounded-3xl p-6 shadow-md">
              <div className="flex justify-between items-center mb-5 pb-3.5 border-b border-gray-100">
                <h2 className="text-xl font-semibold text-gray-900 m-0">베팅 금액 선택</h2>
              </div>
              <div className="grid grid-cols-4 gap-3 mb-6">
                {[50, 100, 200, 500].map(bet => (
                  <button
                    key={bet}
                    className={`flex flex-col items-center gap-2 py-5 px-3 border-none rounded-2xl cursor-pointer transition-all ${currentBet === bet ? 'bg-gradient-to-br from-blue-500 to-purple-500 shadow-lg' : 'bg-gray-100 hover:bg-gray-200 hover:-translate-y-0.5'}`}
                    onClick={() => setCurrentBet(bet)}
                    disabled={bet > chips}
                  >
                    <span className="text-3xl">🪙</span>
                    <span className={`text-lg font-bold ${currentBet === bet ? 'text-white' : 'text-gray-900'}`}>{bet}</span>
                  </button>
                ))}
              </div>
              <button onClick={startGame} className="w-full py-4 bg-gradient-to-br from-blue-500 to-purple-500 text-white border-none rounded-full text-lg font-semibold cursor-pointer hover:scale-[1.02] hover:shadow-lg">
                🎴 게임 시작
              </button>
            </div>

            <div className="bg-gray-50 rounded-3xl p-6 shadow-md">
              <div className="flex justify-between items-center mb-5 pb-3.5 border-b border-gray-100">
                <h2 className="text-xl font-semibold text-gray-900 m-0">게임 규칙</h2>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-3 bg-white rounded-xl shadow-sm">
                  <span className="text-2xl flex-shrink-0">🎯</span>
                  <div>
                    <strong className="block text-sm text-gray-900 mb-1">7점 이상</strong>
                    <p className="m-0 text-xs text-gray-400">스톱 가능</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-white rounded-xl shadow-sm">
                  <span className="text-2xl flex-shrink-0">🔥</span>
                  <div>
                    <strong className="block text-sm text-gray-900 mb-1">고 선언</strong>
                    <p className="m-0 text-xs text-gray-400">점수 배수 증가</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-white rounded-xl shadow-sm">
                  <span className="text-2xl flex-shrink-0">⭐</span>
                  <div>
                    <strong className="block text-sm text-gray-900 mb-1">오광 15점</strong>
                    <p className="m-0 text-xs text-gray-400">사광 4점, 삼광 3점</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-white rounded-xl shadow-sm">
                  <span className="text-2xl flex-shrink-0">🎨</span>
                  <div>
                    <strong className="block text-sm text-gray-900 mb-1">족보</strong>
                    <p className="m-0 text-xs text-gray-400">고도리/홍단/청단/초단</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 게임 플레이 화면 */}
      {gameState === 'playing' && (
        <div className="flex flex-col gap-5">
          {/* 상대방 영역 */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-6 shadow-md">
            <div className="flex items-center gap-4 mb-3">
              <div className="text-3xl w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">🤖</div>
              <div className="flex-1">
                <span className="block text-base font-semibold text-gray-900">컴퓨터</span>
                <span className="text-sm text-gray-400">🎴 {computerHand.length}장</span>
              </div>
              <div className="text-center bg-gradient-to-br from-amber-400 to-amber-500 py-2 px-4 rounded-xl shadow-sm">
                <span className="block text-xl font-bold text-gray-900">{computerScore}</span>
                <span className="text-xs text-amber-800">점</span>
              </div>
            </div>
            {/* 컴퓨터가 내는 카드 표시 */}
            {computerPlayingCard && (
              <div className="flex flex-col items-center gap-2 p-4 my-3 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-xl border-2 border-dashed border-blue-500/30 animate-[computerCardSection_0.5s_ease-out]">
                <span className="text-xs font-semibold text-blue-500 uppercase tracking-wide">컴퓨터가 내는 카드</span>
                <div className="animate-[computerCardPop_0.6s_ease-out]">
                  <HwatuCard
                    card={computerPlayingCard}
                    isPlaying={true}
                    size="normal"
                  />
                </div>
              </div>
            )}
            {/* 상대 수집 카드 표시 */}
            <div className="flex flex-col gap-3 mt-3">
              {computerCollected.광.length > 0 && (
                <div className="bg-gradient-to-br from-amber-400/15 to-amber-500/10 rounded-xl p-2 px-3 border-l-4 border-amber-500">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold py-0.5 px-2 rounded-md bg-gradient-to-br from-amber-400 to-amber-500 text-gray-900">광</span>
                    <span className="text-xs font-semibold text-gray-400">{computerCollected.광.length}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {computerCollected.광.map(card => (
                      <HwatuCard key={card.id} card={card} size="mini" />
                    ))}
                  </div>
                </div>
              )}
              {computerCollected.열끗.length > 0 && (
                <div className="bg-gradient-to-br from-red-500/10 to-red-600/5 rounded-xl p-2 px-3 border-l-4 border-red-500">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold py-0.5 px-2 rounded-md bg-gradient-to-br from-red-500 to-red-600 text-white">열끗</span>
                    <span className="text-xs font-semibold text-gray-400">{computerCollected.열끗.length}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {computerCollected.열끗.map(card => (
                      <HwatuCard key={card.id} card={card} size="mini" />
                    ))}
                  </div>
                </div>
              )}
              {computerCollected.띠.length > 0 && (
                <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 rounded-xl p-2 px-3 border-l-4 border-blue-500">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold py-0.5 px-2 rounded-md bg-gradient-to-br from-blue-500 to-blue-600 text-white">띠</span>
                    <span className="text-xs font-semibold text-gray-400">{computerCollected.띠.length}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {computerCollected.띠.map(card => (
                      <HwatuCard key={card.id} card={card} size="mini" />
                    ))}
                  </div>
                </div>
              )}
              {computerCollected.피.length > 0 && (
                <div className="bg-black/5 rounded-xl p-2 px-3 border-l-4 border-gray-400">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold py-0.5 px-2 rounded-md bg-gray-400 text-white">피</span>
                    <span className="text-xs font-semibold text-gray-400">{getCollectedCount(computerCollected).피}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {computerCollected.피.map(card => (
                      <HwatuCard key={card.id} card={card} size="mini" />
                    ))}
                  </div>
                </div>
              )}
              {computerCollected.광.length === 0 && computerCollected.열끗.length === 0 &&
               computerCollected.띠.length === 0 && computerCollected.피.length === 0 && (
                <div className="text-gray-400 text-sm text-center py-4 bg-black/5 rounded-lg">아직 획득한 카드 없음</div>
              )}
            </div>
          </div>

          {/* 바닥 카드 영역 */}
          <div className="bg-gradient-to-br from-green-800 via-green-700 to-green-800 rounded-3xl p-6 shadow-md border-4 border-green-900 relative">
            <div className="flex justify-between items-center mb-5 pb-3.5 border-b border-white/15">
              <h2 className="text-xl font-semibold text-white/90 m-0">바닥</h2>
              <span className="bg-white/15 text-white/80 py-1 px-3 rounded-full text-sm font-medium">{fieldCards.length}장</span>
            </div>
            <div className="min-h-[100px] relative">
              {fieldCards.length === 0 ? (
                <div className="text-white/50 text-center py-8 text-sm">바닥에 카드가 없습니다</div>
              ) : (
                <div className="flex flex-wrap gap-2 justify-center">
                  {fieldCards.map(card => {
                    const isSelectableCard = pendingSelection &&
                      pendingSelection.matchingCards.some(mc => mc.id === card.id);

                    return (
                      <HwatuCard
                        key={card.id}
                        card={card}
                        size="small"
                        isMatched={matchedCards.includes(card.id)}
                        isSelected={isSelectableCard}
                        onClick={isSelectableCard ? () => handleFloorCardSelection(card) : undefined}
                      />
                    );
                  })}
                </div>
              )}
              {showMatchEffect && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-2xl animate-[matchOverlay_1.2s_ease-out_forwards] pointer-events-none z-[100]">
                  <span className="text-4xl font-extrabold text-amber-400 drop-shadow-lg animate-[matchTextPop_1.2s_ease-out_forwards]">{matchEffectText}</span>
                </div>
              )}
              {pendingSelection && (
                <div className="mt-4 py-4 px-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl text-white text-base font-medium text-center animate-[hintBounce_2s_ease-in-out_infinite]">
                  👆 가져갈 카드를 클릭하세요 (2장 중 1장 선택)
                </div>
              )}
            </div>
          </div>

          {/* 플레이어 수집 카드 */}
          <div className="bg-white rounded-3xl p-6 shadow-md mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-900">내 획득 카드</span>
              <span className="bg-gradient-to-br from-green-500 to-green-600 text-white py-1.5 px-4 rounded-full font-bold text-sm shadow-sm">
                {playerScore}점 {goCount > 0 && `(고${goCount})`}
              </span>
            </div>
            <div className="flex flex-col gap-3 mt-3">
              {playerCollected.광.length > 0 && (
                <div className="bg-gradient-to-br from-amber-400/15 to-amber-500/10 rounded-xl p-2 px-3 border-l-4 border-amber-500">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold py-0.5 px-2 rounded-md bg-gradient-to-br from-amber-400 to-amber-500 text-gray-900">광</span>
                    <span className="text-xs font-semibold text-gray-400">{playerCollected.광.length}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {playerCollected.광.map(card => (
                      <HwatuCard key={card.id} card={card} size="mini" />
                    ))}
                  </div>
                </div>
              )}
              {playerCollected.열끗.length > 0 && (
                <div className="bg-gradient-to-br from-red-500/10 to-red-600/5 rounded-xl p-2 px-3 border-l-4 border-red-500">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold py-0.5 px-2 rounded-md bg-gradient-to-br from-red-500 to-red-600 text-white">열끗</span>
                    <span className="text-xs font-semibold text-gray-400">{playerCollected.열끗.length}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {playerCollected.열끗.map(card => (
                      <HwatuCard key={card.id} card={card} size="mini" />
                    ))}
                  </div>
                </div>
              )}
              {playerCollected.띠.length > 0 && (
                <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 rounded-xl p-2 px-3 border-l-4 border-blue-500">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold py-0.5 px-2 rounded-md bg-gradient-to-br from-blue-500 to-blue-600 text-white">띠</span>
                    <span className="text-xs font-semibold text-gray-400">{playerCollected.띠.length}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {playerCollected.띠.map(card => (
                      <HwatuCard key={card.id} card={card} size="mini" />
                    ))}
                  </div>
                </div>
              )}
              {playerCollected.피.length > 0 && (
                <div className="bg-black/5 rounded-xl p-2 px-3 border-l-4 border-gray-400">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold py-0.5 px-2 rounded-md bg-gray-400 text-white">피</span>
                    <span className="text-xs font-semibold text-gray-400">{getCollectedCount(playerCollected).피}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {playerCollected.피.map(card => (
                      <HwatuCard key={card.id} card={card} size="mini" />
                    ))}
                  </div>
                </div>
              )}
              {playerCollected.광.length === 0 && playerCollected.열끗.length === 0 &&
               playerCollected.띠.length === 0 && playerCollected.피.length === 0 && (
                <div className="text-gray-400 text-sm text-center py-4 bg-black/5 rounded-lg">아직 획득한 카드 없음</div>
              )}
            </div>
          </div>

          {/* 플레이어 패 */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-3xl p-6 shadow-md border border-green-300">
            <div className="flex justify-between items-center mb-5 pb-3.5 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-900 m-0">내 패</h2>
              <span className="bg-green-500 text-white py-1 px-3 rounded-full text-sm font-semibold">{playerHand.length}장</span>
              {!pendingSelection && <span className="text-xs text-gray-400 ml-auto mr-2 font-normal">더블클릭으로 바로 내기</span>}
            </div>
            <div className="flex flex-wrap gap-2 justify-center p-2 bg-black/5 rounded-2xl">
              {playerHand.map(card => (
                <HwatuCard
                  key={card.id}
                  card={card}
                  isSelected={selectedCard?.id === card.id}
                  isDisabled={!isPlayerTurn || canStop || playingCard || pendingSelection}
                  isPlaying={playingCard?.id === card.id}
                  onClick={() => selectCard(card)}
                  onDoubleClick={() => handleDoubleClick(card)}
                />
              ))}
            </div>
            {selectedCard && !canStop && !playingCard && !pendingSelection && (
              <button onClick={playCard} className="block w-full max-w-[200px] mx-auto mt-4 py-3.5 px-8 bg-gradient-to-br from-amber-400 to-amber-500 text-gray-900 border-none rounded-full text-base font-bold cursor-pointer shadow-lg hover:scale-[1.02] hover:shadow-xl">
                🎴 카드 내기
              </button>
            )}
          </div>

          {/* 고/스톱 버튼 */}
          {canStop && (
            <div className="flex gap-4 justify-center">
              <button onClick={handleGo} className="flex-1 max-w-[160px] py-4 px-8 border-none rounded-full text-lg font-bold cursor-pointer bg-gradient-to-br from-red-500 to-red-600 text-white shadow-lg hover:scale-105 hover:shadow-xl">🔥 고!</button>
              <button onClick={handleStop} className="flex-1 max-w-[160px] py-4 px-8 border-none rounded-full text-lg font-bold cursor-pointer bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg hover:scale-105 hover:shadow-xl">✋ 스톱</button>
            </div>
          )}
        </div>
      )}

      {/* 결과 화면 */}
      {gameState === 'result' && (
        <div className="flex flex-col gap-5">
          <div className="bg-white rounded-3xl p-6 shadow-md text-center">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{message}</h2>
            </div>

            <div className="flex items-center justify-center gap-6 mb-6 p-6 bg-gray-100 rounded-2xl">
              <div className="text-center flex-1">
                <div className="text-4xl mb-2">😊</div>
                <span className="block text-sm text-gray-400 mb-1">나</span>
                <span className="block text-2xl font-bold text-green-500">{playerScore}점</span>
                {goCount > 0 && <span className="block text-sm text-amber-500 font-semibold">×{goCount + 1}</span>}
              </div>
              <div className="text-lg font-bold text-gray-400 py-2 px-4 bg-white rounded-xl">VS</div>
              <div className="text-center flex-1">
                <div className="text-4xl mb-2">🤖</div>
                <span className="block text-sm text-gray-400 mb-1">컴퓨터</span>
                <span className="block text-2xl font-bold text-gray-900">{computerScore}점</span>
              </div>
            </div>

            {scoreBreakdown.length > 0 && (
              <div className="bg-gray-100 rounded-2xl p-5 mb-6 text-left">
                <h3 className="text-sm font-semibold text-gray-400 m-0 mb-3">점수 구성</h3>
                <div className="flex flex-col gap-2">
                  {scoreBreakdown.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 px-3 bg-white rounded-lg">
                      <span className="text-sm text-gray-900">{item.name}</span>
                      <span className="text-sm font-semibold text-green-500">+{item.score}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button onClick={() => setGameState('betting')} className="w-full py-4 bg-gradient-to-br from-blue-500 to-purple-500 text-white border-none rounded-full text-lg font-semibold cursor-pointer hover:scale-[1.02] hover:shadow-lg">
              🔄 다시 하기
            </button>
          </div>
        </div>
      )}

      {showLeaderboard && (
        <LeaderboardDisplay gameId="gostop" gameName="맞고" onClose={() => setShowLeaderboard(false)} />
      )}
      {showSubmit && (
        <ScoreSubmitModal
          score={finalScore}
          rank={finalRank}
          onSubmit={handleScoreSubmit}
          onClose={() => setShowSubmit(false)}
        />
      )}
    </div>
  );
};

// ==================== 포커 게임 ====================
const Poker = ({ onBack }) => {
  const [isMuted, setIsMuted] = useState(gameSound.getMuted());
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [gameState, setGameState] = useState('betting'); // betting, dealing, exchange, showdown
  const [chips, setChips] = useState(1000);
  const [currentBet, setCurrentBet] = useState(50);
  const [playerHand, setPlayerHand] = useState([]);
  const [computerHand, setComputerHand] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]);
  const [deck, setDeck] = useState([]);
  const [message, setMessage] = useState('베팅 금액을 선택하세요');
  const [playerRank, setPlayerRank] = useState('');
  const [computerRank, setComputerRank] = useState('');
  const [showSubmit, setShowSubmit] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [finalRank, setFinalRank] = useState(0);

  const suits = ['♠', '♥', '♦', '♣'];
  const values = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];

  const createDeck = () => {
    const newDeck = [];
    suits.forEach(suit => {
      values.forEach((value, idx) => {
        newDeck.push({
          id: `${suit}${value}`,
          suit,
          value,
          numValue: idx + 2,
          isRed: suit === '♥' || suit === '♦'
        });
      });
    });
    return newDeck.sort(() => Math.random() - 0.5);
  };

  const evaluateHand = (hand) => {
    const sortedHand = [...hand].sort((a, b) => b.numValue - a.numValue);
    const values = sortedHand.map(c => c.numValue);
    const suits = sortedHand.map(c => c.suit);

    const isFlush = suits.every(s => s === suits[0]);
    const isStraight = values.every((v, i) => i === 0 || values[i-1] - v === 1) ||
      (values.join(',') === '14,5,4,3,2'); // A-2-3-4-5

    const valueCounts = {};
    values.forEach(v => { valueCounts[v] = (valueCounts[v] || 0) + 1; });
    const counts = Object.values(valueCounts).sort((a, b) => b - a);

    // 족보 판정
    if (isFlush && isStraight && values[0] === 14) return { rank: 10, name: '로얄 플러시' };
    if (isFlush && isStraight) return { rank: 9, name: '스트레이트 플러시' };
    if (counts[0] === 4) return { rank: 8, name: '포카드' };
    if (counts[0] === 3 && counts[1] === 2) return { rank: 7, name: '풀하우스' };
    if (isFlush) return { rank: 6, name: '플러시' };
    if (isStraight) return { rank: 5, name: '스트레이트' };
    if (counts[0] === 3) return { rank: 4, name: '트리플' };
    if (counts[0] === 2 && counts[1] === 2) return { rank: 3, name: '투페어' };
    if (counts[0] === 2) return { rank: 2, name: '원페어' };
    return { rank: 1, name: '하이카드' };
  };

  useEffect(() => {
    gameSound.init();
  }, []);

  const startGame = () => {
    if (currentBet > chips) {
      setMessage('칩이 부족합니다!');
      return;
    }

    gameSound.playGameStart();
    const newDeck = createDeck();
    setDeck(newDeck.slice(10));
    setPlayerHand(newDeck.slice(0, 5));
    setComputerHand(newDeck.slice(5, 10));
    setSelectedCards([]);
    setPlayerRank('');
    setComputerRank('');
    setGameState('exchange');
    setMessage('교환할 카드를 선택하세요 (최대 3장)');
  };

  const toggleCardSelection = (cardId) => {
    if (selectedCards.includes(cardId)) {
      setSelectedCards(selectedCards.filter(id => id !== cardId));
    } else if (selectedCards.length < 3) {
      setSelectedCards([...selectedCards, cardId]);
    }
    gameSound.playClick();
  };

  const exchangeCards = () => {
    gameSound.playFlip();

    // 플레이어 카드 교환
    let newPlayerHand = playerHand.filter(c => !selectedCards.includes(c.id));
    const cardsNeeded = 5 - newPlayerHand.length;
    newPlayerHand = [...newPlayerHand, ...deck.slice(0, cardsNeeded)];

    // 컴퓨터 AI: 페어 이하면 높은 카드 2장 유지
    let newComputerHand = [...computerHand];
    const compEval = evaluateHand(computerHand);
    if (compEval.rank < 3) {
      const sorted = [...computerHand].sort((a, b) => b.numValue - a.numValue);
      newComputerHand = sorted.slice(0, 2);
      newComputerHand = [...newComputerHand, ...deck.slice(cardsNeeded, cardsNeeded + 3)];
    }

    setPlayerHand(newPlayerHand);
    setComputerHand(newComputerHand);
    setDeck(deck.slice(cardsNeeded + 3));

    // 승부
    setTimeout(() => {
      const pEval = evaluateHand(newPlayerHand);
      const cEval = evaluateHand(newComputerHand);
      setPlayerRank(pEval.name);
      setComputerRank(cEval.name);

      let winAmount = 0;
      if (pEval.rank > cEval.rank) {
        winAmount = currentBet;
        setMessage(`승리! ${pEval.name}로 이겼습니다! +${winAmount}`);
        gameSound.playWin();
      } else if (pEval.rank < cEval.rank) {
        winAmount = -currentBet;
        setMessage(`패배... ${cEval.name}에게 졌습니다. ${winAmount}`);
        gameSound.playLose();
      } else {
        // 같은 족보면 높은 카드 비교
        const pHigh = Math.max(...newPlayerHand.map(c => c.numValue));
        const cHigh = Math.max(...newComputerHand.map(c => c.numValue));
        if (pHigh > cHigh) {
          winAmount = currentBet;
          setMessage(`승리! 높은 카드로 이겼습니다! +${winAmount}`);
          gameSound.playWin();
        } else if (pHigh < cHigh) {
          winAmount = -currentBet;
          setMessage(`패배... 높은 카드에서 졌습니다. ${winAmount}`);
          gameSound.playLose();
        } else {
          setMessage('무승부!');
          gameSound.playDraw();
        }
      }

      const newChips = chips + winAmount;
      setChips(newChips);
      setGameState('showdown');

      if (newChips >= 2000) {
        setFinalScore(newChips);
        setFinalRank(Leaderboard.getRank('poker', newChips));
        setShowSubmit(true);
      }
    }, 500);
  };

  const handleScoreSubmit = (name) => {
    Leaderboard.addScore('poker', name, finalScore);
    setShowSubmit(false);
  };

  const toggleSound = () => {
    const muted = gameSound.toggleMute();
    setIsMuted(muted);
  };

  return (
    <div className="max-w-[600px] mx-auto p-4 text-center">
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
        <button onClick={() => { gameSound.playClick(); onBack(); }} className="bg-transparent border-none text-blue-500 text-base cursor-pointer p-2 rounded-lg hover:bg-blue-50">← 뒤로</button>
        <h2 className="text-2xl font-semibold text-gray-900 m-0">포커</h2>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowLeaderboard(true)} className="py-2 px-4 border-none rounded-full text-sm font-medium cursor-pointer bg-gradient-to-br from-amber-400 to-amber-500 text-gray-900 hover:scale-105">🏆</button>
          <span className="text-sm text-gray-500 bg-gray-100 py-2 px-4 rounded-full">칩: {chips}</span>
          <SoundToggle isMuted={isMuted} onToggle={toggleSound} />
        </div>
      </div>

      <div className="text-white text-lg font-medium mb-4 p-2">{message}</div>

      {gameState === 'betting' && (
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <p className="text-gray-600">베팅 금액:</p>
          <div className="flex gap-2">
            {[25, 50, 100, 200].map(bet => (
              <button
                key={bet}
                className={`py-2 px-4 border-none rounded-lg text-base font-medium cursor-pointer ${currentBet === bet ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                onClick={() => setCurrentBet(bet)}
                disabled={bet > chips}
              >
                {bet}
              </button>
            ))}
          </div>
          <button onClick={startGame} className="bg-gray-900 text-white border-none py-3 px-6 rounded-lg text-base font-medium cursor-pointer hover:bg-gray-700">게임 시작</button>
        </div>
      )}

      {(gameState === 'exchange' || gameState === 'showdown') && (
        <div className="bg-gradient-to-br from-green-800 to-green-700 rounded-2xl p-6 mb-6">
          <div className="mb-6">
            <p className="text-white/70 text-sm mb-2">상대 패 {computerRank && `- ${computerRank}`}</p>
            <div className="flex justify-center gap-2 flex-wrap min-h-[80px]">
              {computerHand.map((card, idx) => (
                <div
                  key={card.id}
                  className={`w-14 h-20 bg-white rounded-lg flex flex-col items-center justify-center text-lg font-semibold cursor-pointer shadow-lg gap-1 ${card.isRed ? 'text-red-600' : 'text-gray-900'} ${gameState === 'showdown' ? '' : 'bg-gradient-to-br from-blue-500 to-purple-500 text-white'}`}
                >
                  {gameState === 'showdown' ? (
                    <>
                      <span>{card.value}</span>
                      <span className="text-2xl">{card.suit}</span>
                    </>
                  ) : '🂠'}
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-white/70 text-sm mb-2">내 패 {playerRank && `- ${playerRank}`}</p>
            <div className="flex justify-center gap-2 flex-wrap">
              {playerHand.map(card => (
                <div
                  key={card.id}
                  className={`w-14 h-20 bg-white rounded-lg flex flex-col items-center justify-center text-lg font-semibold cursor-pointer shadow-lg gap-1 ${card.isRed ? 'text-red-600' : 'text-gray-900'} ${selectedCards.includes(card.id) ? '-translate-y-2.5 shadow-xl border-2 border-blue-500' : 'hover:-translate-y-1'}`}
                  onClick={() => gameState === 'exchange' && toggleCardSelection(card.id)}
                >
                  <span>{card.value}</span>
                  <span className="text-2xl">{card.suit}</span>
                </div>
              ))}
            </div>
          </div>

          {gameState === 'exchange' && (
            <div className="mt-6">
              <button onClick={exchangeCards} className="bg-gray-900 text-white border-none py-3 px-6 rounded-lg text-base font-medium cursor-pointer hover:bg-gray-700">
                {selectedCards.length > 0 ? `${selectedCards.length}장 교환` : '교환 안함'}
              </button>
            </div>
          )}

          {gameState === 'showdown' && (
            <button onClick={() => setGameState('betting')} className="mt-6 bg-gray-900 text-white border-none py-3 px-6 rounded-lg text-base font-medium cursor-pointer hover:bg-gray-700">다시 하기</button>
          )}
        </div>
      )}

      {showLeaderboard && (
        <LeaderboardDisplay gameId="poker" gameName="포커" onClose={() => setShowLeaderboard(false)} />
      )}
      {showSubmit && (
        <ScoreSubmitModal
          score={finalScore}
          rank={finalRank}
          onSubmit={handleScoreSubmit}
          onClose={() => setShowSubmit(false)}
        />
      )}
    </div>
  );
};

// ==================== 메인 게임 컴포넌트 ====================
const Games = () => {
  const [selectedGame, setSelectedGame] = useState(null);
  const [isMuted, setIsMuted] = useState(gameSound.getMuted());
  const [showAllRankings, setShowAllRankings] = useState(false);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    gameSound.init();
    // 서버에서 모든 점수 가져오기
    Leaderboard.fetchAllScores().then(() => {
      forceUpdate(n => n + 1); // 점수 로드 후 UI 업데이트
    });
  }, []);

  const games = [
    { id: 'memory', name: '메모리 게임', icon: '🧠', desc: '카드를 뒤집어 짝을 맞춰보세요', component: MemoryGame },
    { id: 'tictactoe', name: '틱택토', icon: '⭕', desc: '3개를 먼저 연결하면 승리!', component: TicTacToe },
    { id: 'sudoku', name: '수도쿠', icon: '🔢', desc: '숫자 퍼즐의 정석', component: Sudoku },
    { id: 'gostop', name: '고스톱', icon: '🎴', desc: '화투로 즐기는 맞고', component: GoStop },
    { id: 'poker', name: '포커', icon: '🃏', desc: '5장 드로우 포커', component: Poker },
  ];

  const handleGameSelect = (gameId) => {
    gameSound.playClick();
    setSelectedGame(gameId);
  };

  const toggleSound = () => {
    const muted = gameSound.toggleMute();
    setIsMuted(muted);
  };

  if (selectedGame) {
    const Game = games.find(g => g.id === selectedGame)?.component;
    return <Game onBack={() => { gameSound.playClick(); setSelectedGame(null); }} />;
  }

  return (
    <div className="max-w-[1200px] mx-auto p-8">
      <div className="text-center mb-12">
        <div className="flex items-center justify-center gap-4 mb-2 flex-wrap">
          <h1 className="text-4xl font-semibold text-gray-900 m-0">게임</h1>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowAllRankings(true)} className="py-2 px-4 border-none rounded-full text-sm font-medium cursor-pointer bg-gradient-to-br from-amber-400 to-amber-500 text-gray-900 hover:scale-105 hover:shadow-lg">🏆 전체 랭킹</button>
            <SoundToggle isMuted={isMuted} onToggle={toggleSound} />
          </div>
        </div>
        <p className="text-lg text-gray-500">다양한 미니게임을 즐겨보세요</p>
      </div>

      <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-6">
        {games.map(game => (
          <div
            key={game.id}
            className="bg-white border border-gray-200 rounded-2xl p-8 text-center transition-all cursor-pointer hover:-translate-y-1 hover:shadow-xl hover:border-blue-500"
            onClick={() => handleGameSelect(game.id)}
          >
            <div className="text-6xl mb-4 flex items-center justify-center h-[4.5rem]">{game.icon}</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{game.name}</h3>
            <p className="text-sm text-gray-500 leading-relaxed mb-3">{game.desc}</p>
            {Leaderboard.getScores(game.id).length > 0 && (
              <div className="text-sm text-amber-500 font-medium py-2 px-4 bg-amber-100 rounded-full inline-block">
                🏆 최고: {Leaderboard.getScores(game.id)[0]?.score.toLocaleString()}
              </div>
            )}
          </div>
        ))}
      </div>

      {showAllRankings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4" onClick={() => setShowAllRankings(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-[500px] w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold text-gray-900 m-0">🏆 전체 게임 랭킹</h3>
              <button className="bg-transparent border-none text-2xl text-gray-400 cursor-pointer p-1 hover:text-gray-700" onClick={() => setShowAllRankings(false)}>×</button>
            </div>
            <div className="flex flex-col gap-6">
              {games.map(game => {
                const scores = Leaderboard.getScores(game.id);
                return (
                  <div key={game.id} className="bg-gray-50 rounded-xl p-4">
                    <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">{game.icon} {game.name}</h4>
                    {scores.length === 0 ? (
                      <p className="text-center text-gray-400 py-4">기록 없음</p>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {scores.slice(0, 3).map((entry, idx) => (
                          <div key={entry.id} className={`flex items-center gap-3 p-2 bg-white rounded-lg ${idx === 0 ? 'bg-gradient-to-r from-amber-100 to-amber-200' : idx === 1 ? 'bg-gradient-to-r from-gray-100 to-gray-200' : 'bg-gradient-to-r from-orange-100 to-orange-200'}`}>
                            <span className="text-xl">
                              {idx === 0 ? '🥇' : idx === 1 ? '🥈' : '🥉'}
                            </span>
                            <span className="flex-1 font-medium text-gray-900">{entry.userName || entry.name}</span>
                            <span className="font-semibold text-blue-500">{entry.score.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Games;
